import {
    type ClassValue,
    clsx,
} from 'clsx';
import { toast } from 'sonner';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function readLocalStorage(key: string): string | undefined {
    const mem = localStorage.getItem(key);
    if (mem === undefined || mem === null) {
        return undefined;
    }

    try {
    // Try to parse as JSON first (new format)
        const parsed = JSON.parse(mem);
        return parsed?.item ?? undefined;
    } catch (error) {
    // If JSON.parse fails, treat it as a plain string (old format)
    // This provides backward compatibility
        return mem || undefined;
    }
}

export function setLocalStorage(key: string, value: string | null | undefined) {
    localStorage.setItem(key, JSON.stringify({ item: value ?? null }));
}

export function clearLocalStorage(key: string) {
    setLocalStorage(key, null);
}

// Helper to read a value from sessionStorage using the same JSON wrapper format
function readSessionStorage(key: string): string | undefined {
    const mem = sessionStorage.getItem(key);
    if (mem === undefined || mem === null) {
        return undefined;
    }
    try {
        const parsed = JSON.parse(mem);
        return parsed?.item ?? undefined;
    } catch {
        return mem || undefined;
    }
}

function setSessionStorage(key: string, value: string | null | undefined) {
    sessionStorage.setItem(key, JSON.stringify({ item: value ?? null }));
}

function clearSessionStorage(key: string) {
    sessionStorage.removeItem(key);
}

// Token management: rememberMe controls localStorage vs sessionStorage
// A flag in localStorage tracks which storage holds the tokens.
const STORAGE_MODE_KEY = 'auth-storage-mode';

export function getTokens() {
    const mode = localStorage.getItem(STORAGE_MODE_KEY);
    const read = mode === 'session' ? readSessionStorage : readLocalStorage;

    const accessToken = read('access-token');
    const refreshToken = read('refresh-token');

    // Check for old format token and migrate if needed
    const oldToken = readLocalStorage('app-auth');
    if (oldToken && !accessToken) {
        clearLocalStorage('app-auth');
        return { accessToken: undefined, refreshToken: undefined };
    }

    return { accessToken, refreshToken };
}

export function setTokens(accessToken: string, refreshToken: string, rememberMe = true) {
    // Clear tokens from both storages first to avoid stale data
    clearSessionStorage('access-token');
    clearSessionStorage('refresh-token');
    clearLocalStorage('access-token');
    clearLocalStorage('refresh-token');

    if (rememberMe) {
        localStorage.setItem(STORAGE_MODE_KEY, 'local');
        setLocalStorage('access-token', accessToken);
        setLocalStorage('refresh-token', refreshToken);
    } else {
        localStorage.setItem(STORAGE_MODE_KEY, 'session');
        setSessionStorage('access-token', accessToken);
        setSessionStorage('refresh-token', refreshToken);
    }
}

export function clearTokens() {
    // Clear from both storages
    clearLocalStorage('access-token');
    clearLocalStorage('refresh-token');
    clearLocalStorage('app-auth');
    clearSessionStorage('access-token');
    clearSessionStorage('refresh-token');
    localStorage.removeItem(STORAGE_MODE_KEY);
}

export const formatDateForBackend = (date: Date): string => date.toISOString().split('T')[0];

// Helper function to display backend errors in toast
export const displayBackendErrors = (errors: Record<string, string[]>) => {
    Object.entries(errors).forEach(([field, messages]) => {
        messages.forEach((message) => {
            toast.error(`${field.replace('_', ' ').toUpperCase()}: ${message}`);
        });
    });
};
